"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.configureAmplify = exports.buildAmplifyConfig = exports.buildAppSyncConfig = exports.buildAuthConfig = void 0;
const aws_amplify_1 = require("aws-amplify");
const environment_1 = require("./environment");
/**
 * Builds an Amplify Auth configuration based on environment variables
 * @returns The Amplify Auth configuration
 */
const buildAuthConfig = (isWeb) => {
    const userPoolId = (0, environment_1.getCognitoUserPoolId)();
    const clientId = (0, environment_1.getCognitoClientId)();
    const baseUrl = (0, environment_1.getBaseUrl)(isWeb);
    const identityPoolId = (0, environment_1.getIdentityPoolId)();
    const domain = (0, environment_1.getCognitoDomain)();
    let redirectSignIn = [];
    let redirectSignOut = [];
    if (isWeb) {
        const webRedirectUri = `${baseUrl}/oauth-callback`;
        redirectSignIn = [webRedirectUri];
        redirectSignOut = [webRedirectUri];
        if (typeof window !== 'undefined') {
            const currentUrl = window.location.origin + '/oauth-callback';
            if (!redirectSignIn.includes(currentUrl)) {
                redirectSignIn.push(currentUrl);
            }
            if (!redirectSignOut.includes(currentUrl)) {
                redirectSignOut.push(currentUrl);
            }
        }
    }
    else {
        redirectSignIn = ['heartbeat://callback'];
        redirectSignOut = ['heartbeat://signout'];
    }
    return {
        userPoolClientId: clientId,
        userPoolId,
        identityPoolId,
        region: (0, environment_1.getAwsRegion)(),
        oauth: {
            domain,
            scopes: ['aws.cognito.signin.user.admin', 'email', 'openid', 'phone', 'profile'],
            redirectSignIn,
            redirectSignOut,
            responseType: 'code',
            providers: ['Google', 'Apple', 'Microsoft'],
        },
    };
};
exports.buildAuthConfig = buildAuthConfig;
/**
 * Builds an Amplify AppSync configuration based on environment variables
 * @param authenticationType Optional authentication type override
 * @returns The Amplify AppSync configuration
 */
const buildAppSyncConfig = (authenticationType = 'userPool') => ({
    graphqlEndpoint: (0, environment_1.getAppSyncEndpoint)(),
    region: (0, environment_1.getAwsRegion)(),
    authenticationType,
    apiKey: authenticationType === 'apiKey' ? (0, environment_1.getAppSyncApiKey)() : undefined,
});
exports.buildAppSyncConfig = buildAppSyncConfig;
/**
 * Builds a complete Amplify configuration based on environment variables
 * @param options Optional overrides for the configuration
 * @returns The complete Amplify configuration
 */
const buildAmplifyConfig = (options) => {
    const isWeb = options?.isWeb === true;
    const authType = options?.appSync?.authenticationType;
    const authenticationType = authType === 'apiKey' ? 'apiKey' : 'userPool';
    const defaultConfig = {
        auth: (0, exports.buildAuthConfig)(isWeb),
        appSync: (0, exports.buildAppSyncConfig)(authenticationType),
        isWeb,
    };
    if (!defaultConfig.appSync?.graphqlEndpoint) {
        defaultConfig.appSync = undefined;
    }
    return {
        ...defaultConfig,
        ...options,
        auth: options?.auth ? { ...defaultConfig.auth, ...options.auth } : defaultConfig.auth,
        appSync: options?.appSync && defaultConfig.appSync
            ? { ...defaultConfig.appSync, ...options.appSync }
            : options?.appSync || defaultConfig.appSync,
        isWeb,
    };
};
exports.buildAmplifyConfig = buildAmplifyConfig;
/**
 * Configures Amplify globally with the provided configuration
 * This ensures we only have one configured instance across the application
 * @param options Optional configuration options
 * @returns The configured Amplify instance
 */
const configureAmplify = (options) => {
    const config = (0, exports.buildAmplifyConfig)(options);
    const amplifyConfig = {
        Auth: {
            Cognito: {
                userPoolId: config.auth?.userPoolId,
                userPoolClientId: config.auth?.userPoolClientId,
                identityPoolId: config.auth?.identityPoolId,
                loginWith: {
                    oauth: {
                        domain: config.auth?.oauth?.domain,
                        scopes: config.auth?.oauth?.scopes,
                        redirectSignIn: config.auth?.oauth?.redirectSignIn,
                        redirectSignOut: config.auth?.oauth?.redirectSignOut,
                        responseType: config.auth?.oauth?.responseType,
                    },
                },
            },
        },
    };
    if (config.appSync?.graphqlEndpoint) {
        amplifyConfig.API = {
            GraphQL: {
                endpoint: config.appSync.graphqlEndpoint,
                apiKey: config.appSync.apiKey,
                region: config.appSync.region || config.auth?.region,
                defaultAuthMode: config.appSync.authenticationType,
            },
        };
        if (config.appSync.graphqlEndpoint) {
            const wsEndpoint = config.appSync.graphqlEndpoint.replace('https://', 'wss://');
            amplifyConfig.API.GraphQL.websocket = {
                url: wsEndpoint,
                keepAlive: 300000, // 5 minutes
            };
        }
    }
    aws_amplify_1.Amplify.configure(amplifyConfig);
    return aws_amplify_1.Amplify;
};
exports.configureAmplify = configureAmplify;
